import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/data';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';

const ARTICLES_PER_PAGE = 6;

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || categories[0]?.id || 'tiaozhuan-fangfa';
  const [currentPage, setCurrentPage] = useState(1);

  const activeCategory = useMemo(
    () => categories.find((c) => c.id === activeSlug),
    [activeSlug]
  );

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.categoryId === activeSlug),
    [activeSlug]
  );

  const totalPages = Math.ceil(filteredArticles.length / ARTICLES_PER_PAGE);
  const pagedArticles = useMemo(
    () => filteredArticles.slice((currentPage - 1) * ARTICLES_PER_PAGE, currentPage * ARTICLES_PER_PAGE),
    [filteredArticles, currentPage]
  );

  const handleCategoryChange = (categoryId: string) => {
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: activeCategory ? `${activeCategory.name} - 鲍鱼tv跳转导航` : '分类页面',
            description: activeCategory?.description || '',
            url: `https://baoyutv-tiaozhuan.com/category/${activeSlug}`,
          }),
        }}
      />

      <main>
        <section className="py-8 md:py-12 bg-background-100/70">
          <div className="container-site">
            <Breadcrumb />
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mt-2">
              {activeCategory?.name || '分类'}
            </h1>
            {activeCategory && (
              <p className="text-sm text-foreground-500 mt-2">
                {activeCategory.description} · 共 {activeCategory.articleCount} 篇文章
              </p>
            )}
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="container-site">
            {/* Category Tabs */}
            <div className="mb-8 md:mb-10 overflow-x-auto">
              <div className="flex gap-2 min-w-max px-1 py-1 bg-background-100 rounded-full">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      activeSlug === cat.id
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200/70'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Article List */}
            {pagedArticles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {pagedArticles.map((article) => (
                    <ArticleCard key={article.id} {...article} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <nav className="mt-10 flex items-center justify-center gap-2" aria-label="分页导航">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 text-foreground-500 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-200 ${
                          currentPage === page
                            ? 'bg-primary-500 text-background-50'
                            : 'border border-background-200/70 text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 text-foreground-500 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </nav>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}